import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { RoleManagement } from './RoleManagement';
import { UserManagement } from './UserManagement';
import { GlobalSettings } from './GlobalSettings';
import { DepartmentManagement } from './DepartmentManagement';
import { UserKeyRecovery } from './UserKeyRecovery';
import { AuditLogManagement } from './AuditLogManagement';
import { DesktopReleaseManagement } from './DesktopReleaseManagement';
import { Shield, Users, Settings, Layers, AlertTriangle, Key, Clock, Monitor } from 'lucide-react';

export function AdminView() {
    const { isAdmin, settings, hasPermission, hasModuleAccess } = useApp();

    const isSuperAdmin = hasPermission('super:admin') || hasPermission('admin:full');

    // Determine which top-level tabs this user can see
    const ALL_TABS = [
        { key: 'roles', label: 'Roles', icon: Shield, permission: 'admin:view:roles' },
        { key: 'users', label: 'Users', icon: Users, permission: 'admin:view:users' },
        { key: 'departments', label: 'Departments', icon: Layers, permission: 'admin:view:departments' },
        { key: 'settings', label: 'Settings', icon: Settings, permission: 'admin:view:settings' },
        { key: 'security', label: 'E2EE Security', icon: Key, permission: 'admin:view:security' },
        { key: 'audit', label: 'Audit Logs', icon: Clock, permission: 'admin:view:audit' },
        { key: 'desktop', label: 'Desktop Releases', icon: Monitor, permission: 'admin:view:desktop_releases' },
    ] as const;

    const visibleTabs = ALL_TABS.filter(tab => {
        if (isSuperAdmin) return true;
        if (tab.key === 'desktop') return hasModuleAccess('desktop_releases');
        return hasPermission(tab.permission);
    });

    // User must be admin OR have at least one visible tab permission
    const canAccess = isAdmin || visibleTabs.length > 0;

    const [activeTab, setActiveTab] = useState<typeof ALL_TABS[number]['key']>(
        visibleTabs[0]?.key ?? 'roles'
    );

    if (!canAccess) {
        return (
            <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: '#e74c3c'
            }}>
                <Shield size={64} style={{ opacity: 0.5, marginBottom: 16 }} />
                <h2 style={{ fontSize: 24, fontWeight: 600 }}>Access Denied</h2>
                <p style={{ marginTop: 8, color: 'var(--text-muted)' }}>You do not have permission to view this area.</p>
            </div>
        );
    }

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
            <header style={{
                background: 'var(--bg-secondary)',
                borderBottom: '1px solid var(--border-color)',
                padding: '16px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Shield size={24} color="var(--accent-primary)" />
                    <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Admin Dashboard</h1>
                </div>

                <nav style={{ display: 'flex', gap: 4, background: 'var(--bg-tertiary)', padding: 4, borderRadius: 8 }}>
                    {visibleTabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500,
                                background: activeTab === tab.key ? 'var(--bg-secondary)' : 'transparent',
                                color: activeTab === tab.key ? 'var(--accent-primary)' : 'var(--text-muted)',
                                boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </header>

            {/* Development Warning Notice */}
            {!settings?.isDbConnected && (
                <div style={{
                    margin: '16px 24px 0',
                    padding: '12px 16px',
                    background: 'rgba(231, 76, 60, 0.1)',
                    border: '1px solid rgba(231, 76, 60, 0.2)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    color: '#e74c3c'
                }}>
                    <AlertTriangle size={20} />
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                        <span style={{ fontWeight: 700 }}>SECURITY WARNING:</span> This environment is using hardcoded test credentials in <code style={{ background: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '4px' }}>server/index.js</code>. Ensure these are removed and replaced with a persistent database before production deployment.
                    </div>
                </div>
            )}

            <main style={{ flex: 1, overflow: 'auto', padding: 24 }}>
                <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                    {activeTab === 'roles' && <RoleManagement />}
                    {activeTab === 'users' && <UserManagement />}
                    {activeTab === 'departments' && <DepartmentManagement />}
                    {activeTab === 'settings' && <GlobalSettings />}
                    {activeTab === 'security' && <UserKeyRecovery />}
                    {activeTab === 'audit' && <AuditLogManagement />}
                    {activeTab === 'desktop' && <DesktopReleaseManagement />}
                </div>
            </main>
        </div>
    );
}

